import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import MessagePopup from "./MessagePopup";
import apiEndpoints from "../../apiconfig";
import { useSnackbar } from "../../DynamicComponents/SnackbarContext";

export default function GroupMsg() {
  const [campaignName, setCampaignName] = useState(
    "CAMP-" + Math.floor(Math.random() * 100000)
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [groups, setGroups] = useState([]);
  const [templateVariables, setTemplateVariables] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const tabs = ["Single MSG", "Group", "CSV"];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(apiEndpoints.getGroups, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();
        if (data.status && Array.isArray(data.data)) {
          setGroups(data.data);
        } else {
          console.warn("No groups found");
        }
      } catch (err) {
        console.error("Error fetching groups:", err);
      }
    };
    fetchGroups();
  }, []);



  const handleSelectTemplate = (template) => {

    let headerObj = null;

    try {
      const templateJson = JSON.parse(template.template_json);

      if (
        templateJson.components &&
        templateJson.components[0]?.format === "IMAGE" &&
        templateJson.components[0]?.example?.header_handle?.length > 0
      ) {
        const imageUrl = templateJson.components[0].example.header_handle[0];
        headerObj = {
          type: "image",
          link: imageUrl,
        };
      }
    } catch (err) {
      console.warn("Failed to parse template_json:", err);
    }


    setSelectedTemplate({
      ...template,
      header: headerObj, // <--- REQUIRED FOR IMAGE TEMPLATES
    });

    setMessageContent(template.templateBody || template.content || "");
    setIsPopupOpen(false);
    const matches = (template.templateBody || "").match(/{{\d+}}/g) || [];
    const variableFields = matches.map((match) => ({
      placeholder: match,
      value: "",
    }));

    setTemplateVariables(variableFields);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!selectedGroup) newErrors.group = "Please select a group";
    if (!selectedTemplate) newErrors.template = "Please select a template";
    if (!messageContent) newErrors.message = "Message cannot be empty";
    if (templateVariables.some((v) => !v.value.trim()))
      newErrors.variables = "Please fill all variable values";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClear = () => {
    setMessageContent("");
    setSelectedGroup("");
    setSelectedTemplate(null);
    setTemplateVariables([]);
    setErrors({});
    setProgress("");
    setCampaignName("CAMP-" + Math.floor(Math.random() * 100000));
  };

  const handleSendMessage = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setProgress("Sending messages to group...");

    try {
      const response = await fetch(apiEndpoints.sendmessage, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          group_name: selectedGroup,
          template_name: selectedTemplate.name,
          template_id: selectedTemplate.id,
          language: selectedTemplate.languageName || "en",  
          variables: templateVariables.map((v) => v.value),
          header: selectedTemplate.header ?? null,
          buttons: selectedTemplate.buttons || [],
          message_content: messageContent,
        }),
      });

      const result = await response.json();

      if (result.status === "success") {
        setProgress(
          `${result.summary.success} sent,  ${result.summary.failed} failed.`
        );
        showSnackbar(
          `${result.summary.success} messages sent, ${result.summary.failed} failed.`,
          "success"
        );
        handleClear();
      } else {
        showSnackbar(
          `${result.message || "Failed to send group messages"}`,
          "error"
        );
        setProgress("Error sending messages.");
      }
    } catch (error) {
      console.error("Error:", error);
      showSnackbar("Something went wrong while sending messages.", "error");
      setProgress("Error sending messages.");
    } finally {
      setLoading(false);
      setIsDropdownOpen(false);
    }
  };

  return (
    <div
      className="w-full bg-[#F4F5F7] p-4 sm:p-6"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      {/* Form Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Group Details */}
        <div className="bg-white p-5 sm:p-6 rounded-2xl shadow-md border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Group Details
          </h3>

          <div className="space-y-5">
            {/* Group Selection */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Select Group<span className="text-red-500 ml-1">*</span>
              </label>
              <select
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#25D366] focus:outline-none ${errors.group ? "border-red-500" : "border-gray-300"
                  }`}
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
              >
                <option value="">Select Group</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.groupname}>
                    {group.groupname} ({group.total_contacts || 0})
                  </option>
                ))}
              </select>
              {errors.group && (
                <p className="text-red-500 text-xs mt-1">{errors.group}</p>
              )}
            </div>

            {/* Campaign Name */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Campaign Name
              </label>
              <input
                type="text"
                readOnly
                value={campaignName}
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
              />
            </div>
          </div>
        </div>

        {/* Right: Message Composer */}
        <div className="bg-white p-5 sm:p-6 rounded-2xl shadow-md border border-gray-100 flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Message Composer
            </h3>

            {/* Template Selector */}
            <div className="mb-5">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Message Template<span className="text-red-500">*</span>
                </label>
                {messageContent && (
                  <button
                    onClick={() => setMessageContent("")}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <textarea
                readOnly
                onClick={() => setIsPopupOpen(true)}
                value={messageContent}
                placeholder="Click to select a template"
                className={`w-full p-3 border rounded-lg h-40 bg-gray-50 cursor-pointer focus:ring-2 focus:ring-[#25D366] ${errors.template ? "border-red-500" : "border-gray-300"
                  }`}
              />
            </div>

            {/* Template Variables */}
            {templateVariables.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Template Variables
                </h4>
                {templateVariables.map((variable, index) => (
                  <div
                    key={index}
                    className="flex flex-wrap sm:flex-nowrap items-center gap-2 border border-gray-200 p-2 rounded-lg bg-gray-50 mb-2"
                  >
                    <span className="font-mono text-gray-700 text-sm sm:w-16 w-auto sm:text-left text-center">
                      {variable.placeholder}
                    </span>
                    <input
                      type="text"
                      placeholder={`Value for ${variable.placeholder}`}
                      value={variable.value}
                      onChange={(e) => {
                        const updated = [...templateVariables];
                        updated[index].value = e.target.value;
                        setTemplateVariables(updated);
                      }}
                      className="flex-1 w-full border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-[#25D366] focus:outline-none"
                    />
                  </div>
                ))}

                {errors.variables && (
                  <p className="text-red-500 text-xs">{errors.variables}</p>
                )}
              </div>
            )}

            {/* Progress Display */}
            {progress && (
              <div className="p-3 text-sm bg-gray-50 border rounded-md text-gray-700 mb-4">
                {progress}
              </div>
            )}
          </div>

          {/* ✅ Buttons stay inside the card and stack on mobile */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4">
            <button
              onClick={handleClear}
              disabled={loading}
              className="w-full sm:w-auto px-5 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
            >
              Clear
            </button>
            <button
              onClick={handleSendMessage}
              disabled={loading}
              className="w-full sm:w-auto px-6 py-2 flex items-center justify-center bg-[#25D366] text-white rounded-lg hover:bg-[#1CB85C] transition disabled:bg-gray-400"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>Send Message</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Template Popup */}
      {isPopupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-auto">
          <div className="w-full flex justify-center items-start mt-10 md:items-center">
            <MessagePopup
              onClose={() => setIsPopupOpen(false)}
              onSelectTemplate={handleSelectTemplate}
              showOnlyMarketing={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}
